import Button from '@/components/Buttons/Button';
import { useAllAvatars } from '@/pages/avatar/hooks/useAllAvatars';
import { useAvatarsSorting } from '@/pages/avatar/hooks/useAvatarsSorting';
import { Presenter, PresenterUsecase } from '@d-id/types';
import { Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import {
    AvatarList,
    BasicAvatarCard,
    CopySnackbar,
    createClipboardHandler,
    createDragHandlers,
    EmptyState,
    FilterContainer,
    JsonOutputSection,
    LoadingState,
} from './shared/AvatarsOrdering';

export const AvatarStudioOrderingForm = () => {
    const { publicAvatars, isLoading } = useAllAvatars(PresenterUsecase.Video, 'all', true);

    const { sortAvatars } = useAvatarsSorting();

    const [orderedAvatars, setOrderedAvatars] = useState<string[]>([]);
    const [showCopySuccess, setShowCopySuccess] = useState(false);

    const { handleDragStart, handleDragEndCleanup } = createDragHandlers();

    const sortedPublicAvatars = useMemo(() => {
        return sortAvatars(publicAvatars);
    }, [publicAvatars, sortAvatars]);

    useEffect(() => {
        if (sortedPublicAvatars.length > 0) {
            const avatarIds = sortedPublicAvatars.map(avatar => avatar.id);
            setOrderedAvatars(avatarIds);
        }
    }, [sortedPublicAvatars]);

    const displayedAvatars = useMemo(() => {
        if (orderedAvatars.length === 0 && publicAvatars.length > 0) {
            return sortedPublicAvatars;
        }

        return orderedAvatars.map(id => publicAvatars.find(avatar => avatar.id === id)).filter(avatar => avatar != null) as Presenter[];
    }, [orderedAvatars, publicAvatars, sortedPublicAvatars]);

    const handleDragEnd = useCallback(
        (result: DropResult) => {
            handleDragEndCleanup();

            try {
                if (!result.destination || result.destination.index === result.source.index) {
                    return;
                }

                const items = Array.from(orderedAvatars);
                const [reorderedItem] = items.splice(result.source.index, 1);
                items.splice(result.destination.index, 0, reorderedItem);

                setOrderedAvatars(items);
            } catch (error) {
                console.error('Drag error:', error);
            }
        },
        [orderedAvatars, handleDragEndCleanup]
    );

    const generateJsonOutput = useCallback(() => {
        return JSON.stringify(orderedAvatars, null, 2);
    }, [orderedAvatars]);

    const copyToClipboard = createClipboardHandler(generateJsonOutput, setShowCopySuccess);

    const resetOrder = useCallback(() => {
        if (sortedPublicAvatars.length > 0) {
            const avatarIds = sortedPublicAvatars.map(avatar => avatar.id);
            setOrderedAvatars(avatarIds);
        }
    }, [sortedPublicAvatars]);

    if (isLoading) {
        return <LoadingState isLoading={isLoading} />;
    }

    if (publicAvatars.length === 0) {
        return <EmptyState message="No public avatars found." />;
    }

    return (
        <>
            <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary' }}>
                Drag and drop public avatars to reorder them for the Studio. This uses the sorting logic from
                <Typography component="span" sx={{ fontFamily: 'monospace', backgroundColor: 'grey.100', px: 0.5, borderRadius: 0.5 }}>
                    top_avatars_for_lib
                </Typography>{' '}
                feature flag.
                <br />
                <br />
                The generated JSON can be copied and pasted back into{' '}
                <Typography
                    component="a"
                    href="https://app.launchdarkly.com/projects/default/flags/top_avatars_for_lib/targeting?env=test&selected-env=test"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        color: 'primary.main',
                        textDecoration: 'underline',
                        '&:hover': { textDecoration: 'none' },
                    }}>
                    LaunchDarkly's top_avatars_for_lib flag
                </Typography>
                .
            </Typography>

            <FilterContainer>
                <Button variant="outlined" onClick={resetOrder} size="small">
                    Reset to Current Sorting Logic
                </Button>
            </FilterContainer>

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Studio Avatars ({displayedAvatars.length} total public avatars)
            </Typography>

            <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
                <Droppable droppableId="avatars" type="AVATAR">
                    {(provided, snapshot) => (
                        <AvatarList
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            sx={{
                                backgroundColor: snapshot.isDraggingOver ? 'primary.light' : 'transparent',
                                transition: 'background-color 0.2s ease',
                            }}>
                            {displayedAvatars.map((avatar, index) => {
                                if (!avatar) return null;

                                return (
                                    <Draggable key={avatar.id} draggableId={avatar.id} index={index}>
                                        {(provided, snapshot) => (
                                            <BasicAvatarCard avatar={avatar} index={index} provided={provided} snapshot={snapshot} />
                                        )}
                                    </Draggable>
                                );
                            })}
                            {provided.placeholder}
                        </AvatarList>
                    )}
                </Droppable>
            </DragDropContext>

            <JsonOutputSection
                title="JSON Output for LaunchDarkly (top_avatars_for_lib)"
                jsonContent={generateJsonOutput()}
                onCopy={copyToClipboard}
                linkUrl="https://app.launchdarkly.com/projects/default/flags/top_avatars_for_lib/targeting?env=test&selected-env=test"
            />

            <CopySnackbar open={showCopySuccess} onClose={() => setShowCopySuccess(false)} />
        </>
    );
};
